import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, LayoutDashboard, Activity, CheckCheck, TrendingUp, AlertTriangle, Clock } from "lucide-react";
import { Reveal, EASE } from "./Reveal";

const TABS = [
  { id: "showcase-tab-whatsapp", key: "whatsapp", label: "Recordatorio WhatsApp", icon: MessageCircle },
  { id: "showcase-tab-dashboard", key: "dashboard", label: "Panel de facturas", icon: LayoutDashboard },
  { id: "showcase-tab-realtime", key: "realtime", label: "Cobros en vivo", icon: Activity },
];

const WhatsAppScreen = () => (
  <div className="space-y-3">
    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/5 p-3 text-[11px] leading-relaxed text-slate-300">
      Hola <span className="text-emerald-400">Librería Andina</span>, tu factura #0871 por
      <span className="font-semibold text-slate-100"> $ 1,250.00</span> vence en 3 días. Aquí tu link
      de pago: mpago.link/ya871
    </div>
    <div className="ml-auto max-w-[70%] rounded-2xl rounded-tr-sm bg-emerald-600/20 p-3 text-[11px] text-emerald-100">
      Perfecto, lo pago mañana sin falta
      <span className="mt-1 flex items-center justify-end gap-1 text-[9px] text-emerald-400">
        09:15 <CheckCheck className="h-3 w-3" />
      </span>
    </div>
    <div className="mono rounded-lg border border-white/10 bg-white/[0.03] p-2 text-center text-[9px] uppercase tracking-widest text-slate-500">
      Siguiente aviso: día de vencimiento · 9:00 am
    </div>
  </div>
);

const STATUS_STYLES = {
  emerald: { chip: "bg-emerald-500/15", icon: "text-emerald-400", text: "text-emerald-400" },
  amber: { chip: "bg-amber-500/15", icon: "text-amber-400", text: "text-amber-400" },
  orange: { chip: "bg-orange-500/15", icon: "text-orange-400", text: "text-orange-400" },
};

const DashboardScreen = () => (
  <div className="space-y-2.5">
    {[
      { name: "Café Bohemia", amount: "$ 890", status: "Al día", color: "emerald", icon: TrendingUp },
      { name: "Ferretería El Tornillo", amount: "$ 2,340", status: "Vence en 2 días", color: "amber", icon: Clock },
      { name: "Estética Marisol", amount: "$ 460", status: "Vencida 4 días", color: "orange", icon: AlertTriangle },
    ].map((r) => {
      const s = STATUS_STYLES[r.color];
      return (
        <div key={r.name} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="flex items-center gap-2.5">
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${s.chip}`}>
              <r.icon className={`h-3.5 w-3.5 ${s.icon}`} />
            </span>
            <div>
              <p className="text-[11px] font-semibold">{r.name}</p>
              <p className={`text-[9px] ${s.text}`}>{r.status}</p>
            </div>
          </div>
          <span className="font-display text-xs font-bold">{r.amount}</span>
        </div>
      );
    })}
    <div className="mono pt-1 text-center text-[9px] uppercase tracking-widest text-slate-500">
      Por cobrar este mes: $ 3,690
    </div>
  </div>
);

const RealtimeScreen = () => (
  <div className="space-y-3">
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
      <p className="mono text-[9px] uppercase tracking-widest text-emerald-300">Cobrado hace 2 min</p>
      <p className="font-display mt-1 text-lg font-bold text-emerald-400">$ 1,250.00</p>
      <p className="text-[10px] text-slate-400">Librería Andina · Mercado Pago</p>
    </div>
    <div className="space-y-1.5">
      {[82, 64, 91, 73].map((w, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="mono w-14 text-[9px] text-slate-500">Sem {i + 1}</span>
          <div className="h-1.5 flex-1 rounded-full bg-white/5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${w}%` }}
              transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: EASE }}
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
            />
          </div>
          <span className="mono text-[9px] text-emerald-400">{w}%</span>
        </div>
      ))}
    </div>
    <p className="mono text-center text-[9px] uppercase tracking-widest text-slate-500">
      Tasa de cobro a tiempo · últimas 4 semanas
    </p>
  </div>
);

const SCREENS = { whatsapp: WhatsAppScreen, dashboard: DashboardScreen, realtime: RealtimeScreen };

export const Showcase = () => {
  const [active, setActive] = useState("whatsapp");
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const wrapRef = useRef(null);
  const Screen = SCREENS[active];

  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setSpot({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
  };

  return (
    <section id="producto" data-testid="product-showcase-section" className="mx-auto max-w-6xl px-4 py-24 sm:px-6 lg:py-32">
      <Reveal className="text-center">
        <p className="mono mb-4 text-xs uppercase tracking-[0.2em] text-emerald-400">El producto</p>
        <h2 className="font-display mx-auto max-w-2xl text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
          Tu cobranza entera, en la palma de tu mano.
        </h2>
      </Reveal>

      <Reveal delay={0.15} className="mt-10 flex flex-wrap justify-center gap-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={t.id}
            onClick={() => setActive(t.key)}
            className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
              active === t.key
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/20 hover:text-slate-200"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </Reveal>

      <Reveal delay={0.25} className="mt-12">
        <div
          ref={wrapRef}
          onMouseMove={onMove}
          className="relative mx-auto max-w-md overflow-hidden rounded-[2.5rem] border border-white/10 bg-[#131316] p-3"
        >
          <div
            className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-500"
            style={{
              background: `radial-gradient(320px circle at ${spot.x}% ${spot.y}%, rgba(16,185,129,0.14), transparent 70%)`,
            }}
          />
          <div className="rounded-[2rem] border border-white/5 bg-[#0A0A0C] p-5">
            <div className="mx-auto mb-5 h-1 w-16 rounded-full bg-white/15" />
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                data-testid="showcase-phone-screen"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <Screen />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
